import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';

import { type Collectibles, Direction, type QueryCollectiblesArgs } from '@pkg/graphql/codegen/graphql';

import { callGetCollectibles } from '@app/client/resources/marketplace';

interface UseGetCollectiblesArgs {
    variables: QueryCollectiblesArgs;
    disabled?: boolean;
}

export const COLLECTION_COLLECTIBLES_CACHE = 'gql-collection-items';

export function useGetCollectibles({ variables, disabled }: UseGetCollectiblesArgs) {
    const { data, ...rest } = useInfiniteQuery({
        queryKey: [COLLECTION_COLLECTIBLES_CACHE, ...Object.values(variables)],
        queryFn: ({ signal, pageParam }) =>
            callGetCollectibles({
                variables: {
                    ...variables,
                    orderInput: {
                        direction: Direction.Asc, /// TODO: change to Desc listing_price field ordering doesn't count null as higher
                        field: 'listing_price',
                    },
                    paginationInput: {
                        cursor: {
                            cursor: pageParam || null,
                            limit: 100,
                        },
                    },
                },
                settings: { signal },
            }),
        initialPageParam: '',
        getNextPageParam: (response) => response?.data?.collectibles?.cursor || null,
        staleTime: Infinity,
        enabled: !disabled,
        placeholderData: keepPreviousData,
        retry: false,
        select: (response) => {
            const pages = (response.pages.flatMap(({ data }) => data?.collectibles).filter(Boolean) ||
                []) as Collectibles[];
            const pagesData = pages.flatMap((pageData) => pageData?.value);
            return {
                ...response,
                pages,
                pagesData,
            };
        },
    });

    return {
        data,
        pagesData: data?.pagesData,
        ...rest,
    };
}
